import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import AdminEditLeadModal from './admin/AdminEditLeadModal';
import CustomDropdown from './CustomDropdown';

/**
 * Team Hierarchy Modal - Hierarchical view of team submissions
 * 
 * Shows a beautiful, hierarchical view of all team members and their submissions.
 * Used by RSLs and ASMs to manage their teams.
 * 
 * Features:
 * - Hierarchical tree view (RSL → ASM → Users)
 * - Expandable/collapsible sections
 * - Click user → Show their submissions
 * - Click submission → Edit modal
 * - PBFS branded styling
 */
// 🚀 SESSION-LEVEL CACHE (persists across modal opens/closes)
const hierarchyCache = {};
const submissionsCache = {};
const asmChildrenCache = {};  // 🚀 NEW: Cache for lazy-loaded ASM children

const TeamHierarchyModal = ({ 
  isOpen, 
  onClose, 
  userEmail,  // Email of the RSL/ASM viewing the modal
  backendUrl,
  days = 0    // Default to ALL TIME (days <= 0 means all-time)
}) => {
  const [loading, setLoading] = useState(true);
  const [hierarchyData, setHierarchyData] = useState(null);
  const [expandedUsers, setExpandedUsers] = useState(new Set());  // Track which users are expanded
  const [expandedASMs, setExpandedASMs] = useState(new Set());    // Track which ASMs are expanded
  const [userSubmissions, setUserSubmissions] = useState({});     // Cache submissions by user email
  const [loadingSubmissions, setLoadingSubmissions] = useState({}); // Track loading state per user
  const [submissionPaging, setSubmissionPaging] = useState({});   // email -> { limit, offset, total, has_more, next_offset }
  
  // 🚀 NEW: Track ASM children loading state and data
  const [asmChildren, setAsmChildren] = useState({});  // asm_id -> { children, submission_count, loaded }
  const [loadingASMs, setLoadingASMs] = useState({});  // asm_id -> boolean
  
  // Edit submission state
  const [editingSubmission, setEditingSubmission] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  // UI controls - Default to ALL TIME (days=0)
  const [rangeDays, setRangeDays] = useState(days || 0);
  const [perUserLimit, setPerUserLimit] = useState(25);

  // 🚀 OPTIMIZATION: Reset states when modal opens or range changes
  useEffect(() => {
    if (!isOpen) return;
    setExpandedUsers(new Set());
    setExpandedASMs(new Set());
    // 🔧 FIX: Clear ASM children when range changes (cached data is for different range!)
    setAsmChildren({});
    setLoadingASMs({});
    setLoadingSubmissions({});
    // Note: userSubmissions are cleared too since they depend on rangeDays
    setUserSubmissions({});
    setSubmissionPaging({});
  }, [isOpen, rangeDays]);
  
  // Fetch hierarchy data (with session-level caching)
  useEffect(() => {
    if (!isOpen || !userEmail) return;
    
    const cacheKey = `${userEmail}-${rangeDays}`;
    
    // 🚀 OPTIMIZATION: Check session cache first
    if (hierarchyCache[cacheKey]) {
      setHierarchyData(hierarchyCache[cacheKey]);
      setLoading(false);
      return;
    }
    
    const fetchHierarchy = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        // Base64 encode email to bypass WAF
        const encodedEmail = btoa(userEmail);
        const response = await fetch(`${backendUrl}/api/admin/team-hierarchy/${encodedEmail}?days=${rangeDays}`, {
          headers: {
            ...(token && { 'Authorization': `Bearer ${token}` })
          }
        });
        const data = await response.json();
        
        if (data.success) {
          setHierarchyData(data);
          // 🚀 Store in session cache
          hierarchyCache[cacheKey] = data;
        } else {
          console.error('❌ HIERARCHY-MODAL: Failed to fetch hierarchy:', data.error);
        }
      } catch (error) {
        console.error('❌ HIERARCHY-MODAL: Error fetching hierarchy:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchHierarchy();
  }, [isOpen, userEmail, backendUrl, rangeDays]);
  
  // Fetch submissions for a specific user
  const fetchUserSubmissions = async (email, { offset = 0, append = false } = {}) => {
    // 🚀 OPTIMIZATION: Check session cache first
    const cacheKey = `${email}-${offset}`;
    if (!append && (userSubmissions[email] || submissionsCache[cacheKey])) {
      if (submissionsCache[cacheKey] && !userSubmissions[email]) {
        setUserSubmissions(prev => ({ ...prev, [email]: submissionsCache[cacheKey] }));
      }
      return;
    }
    
    setLoadingSubmissions(prev => ({ ...prev, [email]: true }));
    
    try {
      const token = localStorage.getItem('token');
      // Base64 encode email to bypass WAF
      const encodedEmail = btoa(email);
      const response = await fetch(
        `${backendUrl}/api/admin/team-submissions/${encodedEmail}?days=${rangeDays}&limit=${perUserLimit}&offset=${offset}`,
        {
          headers: {
            ...(token && { 'Authorization': `Bearer ${token}` })
          }
        }
      );
      const data = await response.json();
      
      if (data.success) {
        const newSubmissions = append ? [...(userSubmissions[email] || []), ...(data.submissions || [])] : (data.submissions || []);
        
        setUserSubmissions(prev => ({
          ...prev,
          [email]: newSubmissions
        }));
        
        // 🚀 OPTIMIZATION: Store in session cache
        const cacheKey = `${email}-${offset}`;
        submissionsCache[cacheKey] = newSubmissions;
        
        setSubmissionPaging(prev => ({
          ...prev,
          [email]: {
            limit: data.limit,
            offset: data.offset,
            total: data.total,
            has_more: data.has_more,
            next_offset: data.next_offset
          }
        }));
      }
    } catch (error) {
      console.error(`❌ HIERARCHY-MODAL: Error fetching submissions for ${email}:`, error);
    } finally {
      setLoadingSubmissions(prev => ({ ...prev, [email]: false }));
    }
  };
  
  // Toggle user expansion (show/hide submissions)
  const toggleUser = (email) => {
    const newExpanded = new Set(expandedUsers);
    if (newExpanded.has(email)) {
      newExpanded.delete(email);
    } else {
      newExpanded.add(email);
      // Fetch submissions if not already loaded
      fetchUserSubmissions(email);
    }
    setExpandedUsers(newExpanded);
  };
  
  // 🚀 LAZY LOADING: Fetch ASM children on-demand
  const fetchAsmChildren = async (asmId, asmEmail) => {
    // Check session cache first
    const cacheKey = `${asmId}-${rangeDays}`;
    if (asmChildrenCache[cacheKey]) {
      console.log(`📦 ASM-CHILDREN: Using cached data for ${asmId}`);
      setAsmChildren(prev => ({ ...prev, [asmId]: asmChildrenCache[cacheKey] }));
      return;
    }
    
    // Already loading?
    if (loadingASMs[asmId]) return;
    
    setLoadingASMs(prev => ({ ...prev, [asmId]: true }));
    
    try {
      console.log(`🔄 ASM-CHILDREN: Fetching children for ASM ${asmId}`);
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${backendUrl}/api/admin/team-hierarchy/${encodeURIComponent(userEmail)}/asm/${encodeURIComponent(asmId)}?days=${rangeDays}`,
        {
          headers: {
            ...(token && { 'Authorization': `Bearer ${token}` })
          }
        }
      );
      const data = await response.json();
      
      if (data.success) {
        const childrenData = {
          children: data.children || [],
          submission_count: data.submission_count,
          own_submissions: data.own_submissions,
          loaded: true
        };
        
        // Store in component state
        setAsmChildren(prev => ({ ...prev, [asmId]: childrenData }));
        
        // Store in session cache
        asmChildrenCache[cacheKey] = childrenData;
        
        console.log(`✅ ASM-CHILDREN: Loaded ${data.children?.length || 0} children for ${asmId}`);
      }
    } catch (error) {
      console.error(`❌ ASM-CHILDREN: Error fetching children for ${asmId}:`, error);
    } finally {
      setLoadingASMs(prev => ({ ...prev, [asmId]: false }));
    }
  };
  
  // Toggle ASM expansion (show/hide their users) - NOW WITH LAZY LOADING
  const toggleASM = (asmId, asmEmail, childrenLoaded) => {
    const newExpanded = new Set(expandedASMs);
    if (newExpanded.has(asmId)) {
      newExpanded.delete(asmId);
    } else {
      newExpanded.add(asmId);
      // 🚀 LAZY: Fetch children if not already loaded
      if (!childrenLoaded && !asmChildren[asmId]?.loaded) {
        fetchAsmChildren(asmId, asmEmail);
      }
    }
    setExpandedASMs(newExpanded);
  };
  
  // Open edit modal for a submission
  const handleEditSubmission = (submission) => {
    setEditingSubmission(submission);
    setShowEditModal(true);
  };
  
  // Handle successful edit
  const handleSubmissionSaved = (updatedSubmission) => {
    // Best-effort: update cached rows for the email
    const email = (updatedSubmission?.submitted_by_email || updatedSubmission?.submitted_by_email || '').toLowerCase();
    if (email && userSubmissions[email]) {
      setUserSubmissions(prev => ({
        ...prev,
        [email]: (prev[email] || []).map(s =>
          (s._id && updatedSubmission._id && s._id === updatedSubmission._id) ? updatedSubmission : s
        )
      }));
    }
    setShowEditModal(false);
    setEditingSubmission(null);
  };
  
  if (!isOpen) return null;
  
  // Use Portal to render outside header's stacking context
  return createPortal(
    <>
      {/* Modal Backdrop */}
      <div 
        className="fixed inset-0 bg-black/70 z-[9999] flex items-start justify-center overflow-y-auto py-8 px-4 animate-fadeIn"
        onClick={onClose}
      >
        {/* Modal Content */}
        <div 
          className="bg-white rounded-xl max-w-4xl w-full shadow-2xl flex flex-col"
          style={{ maxHeight: 'calc(100vh - 4rem)' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header - Responsive layout */}
          <div className="bg-gradient-to-r from-[var(--pbfs-navy)] to-[var(--pbfs-blue)] text-white p-4 sm:p-6">
            <div className="flex justify-between items-start gap-3">
              <div className="flex-1 min-w-0">
                <h2 className="text-xl sm:text-2xl font-bold">
                  Team Activity
                </h2>
                {hierarchyData && (
                  <p className="text-white/80 text-xs sm:text-sm mt-1 truncate">
                    {hierarchyData.officer.name} • {hierarchyData.total_submissions} submissions
                  </p>
                )}
              </div>
              <button
                onClick={onClose}
                className="bg-white/20 hover:bg-white/30 rounded-full w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center transition-all text-lg sm:text-xl font-bold flex-shrink-0"
              >
                ×
              </button>
            </div>
            
            {/* Filters - Compact row */}
            <div className="mt-3 sm:mt-4 flex flex-wrap gap-2 sm:gap-3 items-center">
              <CustomDropdown
                value={rangeDays}
                minWidth="110px"
                className="flex-1 sm:flex-none"
                onChange={(val) => setRangeDays(parseInt(val, 10))}
                options={[
                  { value: 7, label: '7 days' },
                  { value: 30, label: '30 days' },
                  { value: 90, label: '90 days' },
                  { value: 0, label: 'All time' }
                ]}
              />
              <div className="flex items-center gap-1.5">
                <span className="text-white/60 text-xs whitespace-nowrap">Per user:</span>
                <CustomDropdown
                  value={perUserLimit}
                  minWidth="60px"
                  onChange={(val) => setPerUserLimit(parseInt(val, 10))}
                  options={[
                    { value: 10, label: '10' },
                    { value: 25, label: '25' },
                    { value: 50, label: '50' },
                    { value: 100, label: '100' }
                  ]}
                />
              </div>
            </div>
          </div>
          
          {/* Body - Responsive padding */}
          <div className="p-3 sm:p-6 overflow-y-auto flex-1 min-h-0">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin text-6xl mb-4">⚙️</div>
                <p className="text-[var(--pbfs-navy)]">Loading team hierarchy...</p>
              </div>
            ) : !hierarchyData || hierarchyData.total_submissions === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">📭</div>
                <p className="text-[var(--pbfs-navy)] text-lg font-semibold">No team activity yet</p>
                <p className="text-gray-600 mt-2">Your team hasn't submitted any inspections in the last {rangeDays || 'selected'} days.</p>
              </div>
            ) : (
              <div className="space-y-2 sm:space-y-3">
                {hierarchyData.hierarchy.map((item, index) => {
                  // 🔧 NEW: Handle "leader" type - officer's own submissions
                  if (item.type === 'leader') {
                    return (
                      <div key={item.email || index} className="border-2 border-[var(--pbfs-green)] rounded-lg overflow-hidden bg-gradient-to-r from-[var(--pbfs-green)]/10 to-[var(--pbfs-blue)]/5">
                        <div
                          onClick={() => toggleUser(item.email)}
                          className="p-3 sm:p-4 cursor-pointer hover:bg-[var(--pbfs-green)]/20 transition-all flex justify-between items-center gap-2"
                        >
                          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                            <span className="text-lg sm:text-2xl flex-shrink-0">{expandedUsers.has(item.email) ? '▼' : '▶'}</span>
                            <div className="min-w-0 flex-1">
                              <div className="font-bold text-[var(--pbfs-navy)] flex items-center gap-1.5 sm:gap-2 flex-wrap">
                                <span className="text-sm sm:text-base">⭐</span>
                                <span className="truncate text-sm sm:text-base">{item.name}</span>
                                <span className="text-[10px] sm:text-xs bg-[var(--pbfs-green)] text-white px-1.5 sm:px-2 py-0.5 rounded flex-shrink-0">Your Activity</span>
                              </div>
                              <div className="text-[10px] sm:text-sm text-gray-600 truncate">{item.email}</div>
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <div className="text-xl sm:text-2xl font-bold text-[var(--pbfs-green)]">{item.submission_count}</div>
                            <div className="text-[10px] sm:text-xs text-gray-600 whitespace-nowrap">{item.total_points} pts</div>
                          </div>
                        </div>
                        
                        {/* Leader's submissions (if expanded) */}
                        {expandedUsers.has(item.email) && (
                          <div className="bg-white p-2 sm:p-4 space-y-2">
                            {loadingSubmissions[item.email] ? (
                              <div className="text-center py-4">
                                <div className="animate-spin text-2xl">⚙️</div>
                                <p className="text-sm text-gray-600 mt-2">Loading your submissions...</p>
                              </div>
                            ) : (userSubmissions[item.email] || []).length === 0 ? (
                              <div className="text-center py-4 text-gray-500 text-sm">
                                No submissions found
                              </div>
                            ) : (
                              <>
                                {(userSubmissions[item.email] || []).map((submission, idx) => (
                                  <div
                                    key={submission.id || idx}
                                    className="bg-gray-50 rounded-lg p-2 sm:p-3 hover:bg-gray-100 transition-all flex items-center gap-2 sm:gap-3"
                                  >
                                    <div className="flex-1 min-w-0">
                                      <div className="font-semibold text-xs sm:text-sm text-[var(--pbfs-navy)] truncate">
                                        {submission.business_name || submission.site_street_address || 'Submission'}
                                      </div>
                                      <div className="text-[10px] sm:text-xs text-gray-600">
                                        {new Date(submission.created_at).toLocaleString('en-US', {
                                          month: 'short',
                                          day: 'numeric',
                                          hour: 'numeric',
                                          minute: '2-digit',
                                          timeZone: 'America/Chicago'
                                        })}
                                      </div>
                                      {submission.status && (
                                        <span className={`text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded inline-block mt-0.5 ${
                                          submission.status === 'approved' ? 'bg-green-100 text-green-800' :
                                          submission.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                          'bg-gray-100 text-gray-800'
                                        }`}>
                                          {submission.status}
                                        </span>
                                      )}
                                    </div>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditSubmission(submission);
                                      }}
                                      className="bg-[var(--pbfs-blue)] hover:bg-[var(--pbfs-navy)] text-white px-2 sm:px-3 py-1.5 rounded-lg text-xs sm:text-sm font-semibold transition-all flex items-center gap-1 flex-shrink-0"
                                    >
                                      <span>✏️</span>
                                      <span className="hidden sm:inline">Edit</span>
                                    </button>
                                  </div>
                                ))}
                                <UserPagingControls 
                                  paging={submissionPaging[item.email]} 
                                  onLoadMore={() => {
                                    const p = submissionPaging[item.email];
                                    if (p?.has_more && p?.next_offset != null) {
                                      fetchUserSubmissions(item.email, { offset: p.next_offset, append: true });
                                    }
                                  }} 
                                  isLoading={loadingSubmissions[item.email]} 
                                />
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  }
                  
                  if (item.type === 'asm') {
                    // ASM node with children (supports lazy loading)
                    const asmId = item.id || item.email;  // Use ID for lazy loading
                    const isExpanded = expandedASMs.has(asmId);
                    const isLoading = loadingASMs[asmId];
                    
                    // 🚀 LAZY: Get children from lazy-loaded data OR from initial data
                    const lazyData = asmChildren[asmId];
                    const childrenLoaded = item.children_loaded || lazyData?.loaded;
                    const displayChildren = lazyData?.children || item.children || [];
                    const displaySubmissionCount = lazyData?.submission_count ?? item.submission_count;
                    const displayOwnSubmissions = lazyData?.own_submissions ?? item.own_submissions ?? 0;
                    
                    return (
                      <div key={asmId || index} className="border border-[var(--pbfs-blue)] rounded-lg overflow-hidden">
                        {/* ASM Header - More compact on mobile */}
                        <div
                          onClick={() => toggleASM(asmId, item.email, childrenLoaded)}
                          className="bg-[var(--pbfs-blue)]/10 p-3 sm:p-4 cursor-pointer hover:bg-[var(--pbfs-blue)]/20 transition-all flex justify-between items-center gap-2"
                        >
                          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                            <span className="text-lg sm:text-2xl flex-shrink-0">
                              {isLoading ? (
                                <span className="animate-spin inline-block">⏳</span>
                              ) : isExpanded ? '▼' : '▶'}
                            </span>
                            <div className="min-w-0 flex-1">
                              <div className="font-bold text-[var(--pbfs-navy)] flex items-center gap-1.5 sm:gap-2 flex-wrap">
                                <span className="text-sm sm:text-base">👔</span>
                                <span className="truncate text-sm sm:text-base">{item.name}</span>
                                <span className="text-[10px] sm:text-xs bg-[var(--pbfs-blue)] text-white px-1.5 sm:px-2 py-0.5 rounded flex-shrink-0">RD</span>
                              </div>
                              <div className="text-[10px] sm:text-sm text-gray-600 truncate">{item.email}</div>
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <div className="text-xl sm:text-2xl font-bold text-[var(--pbfs-green)]">
                              {displaySubmissionCount !== null ? displaySubmissionCount : (
                                <span className="text-gray-400 text-base">...</span>
                              )}
                            </div>
                            <div className="text-[10px] sm:text-xs text-gray-600 whitespace-nowrap">{item.team_size} members</div>
                          </div>
                        </div>
                        
                        {/* ASM's Users (if expanded) */}
                        {isExpanded && (
                          <div className="bg-white p-2 sm:p-4 space-y-2">
                            {isLoading ? (
                              // 🚀 SKELETON LOADING STATE
                              <div className="space-y-2">
                                {[1, 2, 3].map((i) => (
                                  <div key={i} className="border border-gray-200 rounded-lg p-3 animate-pulse">
                                    <div className="flex items-center gap-3">
                                      <div className="w-8 h-8 bg-gray-200 rounded"></div>
                                      <div className="flex-1">
                                        <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
                                        <div className="h-3 bg-gray-100 rounded w-48"></div>
                                      </div>
                                      <div className="h-6 w-8 bg-gray-200 rounded"></div>
                                    </div>
                                  </div>
                                ))}
                                <p className="text-center text-sm text-gray-500 py-2">Loading team members...</p>
                              </div>
                            ) : (
                              <>
                                {/* 🔧 FIX: Show ASM's own submissions first (personal activity) */}
                                {displayOwnSubmissions > 0 && (
                                  <UserNode
                                    key={item.email}
                                    user={{
                                      email: item.email,
                                      name: item.name + ' (Personal)',
                                      submission_count: displayOwnSubmissions,
                                      total_points: displayOwnSubmissions,
                                      level: 1,
                                      is_self: true
                                    }}
                                    isExpanded={expandedUsers.has(item.email)}
                                    onToggle={() => toggleUser(item.email)}
                                    submissions={userSubmissions[item.email] || []}
                                    isLoadingSubmissions={loadingSubmissions[item.email] || false}
                                    onEditSubmission={handleEditSubmission}
                                    paging={submissionPaging[item.email]}
                                    onLoadMore={() => {
                                      const p = submissionPaging[item.email];
                                      if (p?.has_more && p?.next_offset != null) {
                                        fetchUserSubmissions(item.email, { offset: p.next_offset, append: true });
                                      }
                                    }}
                                    indented={true}
                                  />
                                )}
                                
                                {/* Then show ASM's team members */}
                                {displayChildren.length > 0 ? (
                                  displayChildren.map((child, childIndex) => (
                                    <UserNode
                                      key={child.email || childIndex}
                                      user={child}
                                      isExpanded={expandedUsers.has(child.email)}
                                      onToggle={() => toggleUser(child.email)}
                                      submissions={userSubmissions[child.email] || []}
                                      isLoadingSubmissions={loadingSubmissions[child.email] || false}
                                      onEditSubmission={handleEditSubmission}
                                      paging={submissionPaging[child.email]}
                                      onLoadMore={() => {
                                        const p = submissionPaging[child.email];
                                        if (p?.has_more && p?.next_offset != null) {
                                          fetchUserSubmissions(child.email, { offset: p.next_offset, append: true });
                                        }
                                      }}
                                      indented={true}
                                    />
                                  ))
                                ) : !isLoading && displayOwnSubmissions === 0 && (
                                  <div className="text-center py-4 text-gray-500 text-sm">
                                    No team members with submissions
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  } else {
                    // Direct user node
                    return (
                      <UserNode
                        key={item.email || index}
                        user={item}
                        isExpanded={expandedUsers.has(item.email)}
                        onToggle={() => toggleUser(item.email)}
                        submissions={userSubmissions[item.email] || []}
                        isLoadingSubmissions={loadingSubmissions[item.email] || false}
                        onEditSubmission={handleEditSubmission}
                        paging={submissionPaging[item.email]}
                        onLoadMore={() => {
                          const p = submissionPaging[item.email];
                          if (p?.has_more && p?.next_offset != null) {
                            fetchUserSubmissions(item.email, { offset: p.next_offset, append: true });
                          }
                        }}
                        indented={false}
                      />
                    );
                  }
                })}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Edit Submission Modal */}
      <AdminEditLeadModal
        isOpen={showEditModal}
        inspection={editingSubmission}
        backendUrl={backendUrl}
        onClose={() => {
          setShowEditModal(false);
          setEditingSubmission(null);
        }}
        onSaved={handleSubmissionSaved}
      />
    </>,
    document.body
  );
};

/**
 * User Node Component - Individual user in the hierarchy
 */
const UserNode = ({ user, isExpanded, onToggle, submissions, isLoadingSubmissions, onEditSubmission, paging, onLoadMore, indented }) => {
  return (
    <div className={`border border-[var(--pbfs-green)]/30 rounded-lg overflow-hidden ${indented ? 'ml-2 sm:ml-4' : ''}`}>
      {/* User Header - More compact */}
      <div
        onClick={onToggle}
        className="bg-[var(--pbfs-green)]/10 p-2 sm:p-3 cursor-pointer hover:bg-[var(--pbfs-green)]/20 transition-all flex justify-between items-center gap-2"
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <span className="text-base sm:text-lg flex-shrink-0">{isExpanded ? '▼' : '▶'}</span>
          <div className="min-w-0 flex-1">
            <div className="font-bold text-[var(--pbfs-navy)] flex items-center gap-1 sm:gap-2 flex-wrap">
              <span className="text-sm sm:text-base">👤</span>
              <span className="truncate text-sm sm:text-base">{user.name}</span>
              {user.level > 1 && (
                <span className="text-[10px] sm:text-xs bg-[var(--pbfs-green)] text-white px-1.5 py-0.5 rounded flex-shrink-0">
                  Lvl {user.level}
                </span>
              )}
            </div>
            <div className="text-[10px] sm:text-xs text-gray-600 truncate">{user.email}</div>
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <div className="text-lg sm:text-xl font-bold text-[var(--pbfs-green)]">{user.submission_count}</div>
          <div className="text-[10px] sm:text-xs text-gray-600 whitespace-nowrap">{user.total_points} pts</div>
        </div>
      </div>
      
      {/* User's Submissions (if expanded) */}
      {isExpanded && (
        <div className="bg-white p-2 sm:p-3 space-y-1.5 sm:space-y-2">
          {isLoadingSubmissions ? (
            <div className="text-center py-4">
              <div className="animate-spin text-2xl">⚙️</div>
              <p className="text-sm text-gray-600 mt-2">Loading submissions...</p>
            </div>
          ) : submissions.length === 0 ? (
            <div className="text-center py-4 text-gray-500 text-sm">
              No submissions found
            </div>
          ) : (
            <>
              {submissions.map((submission, idx) => (
              <div
                key={submission.id || idx}
                className="bg-gray-50 rounded-lg p-2 sm:p-3 hover:bg-gray-100 transition-all flex items-center gap-2 sm:gap-3"
              >
                {/* Details - Clean, no thumbnail */}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-xs sm:text-sm text-[var(--pbfs-navy)] truncate">
                    {submission.business_name || submission.site_street_address || 'Submission'}
                  </div>
                  <div className="text-[10px] sm:text-xs text-gray-600">
                    {new Date(submission.created_at).toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                      timeZone: 'America/Chicago'
                    })}
                  </div>
                  {submission.status && (
                    <span className={`text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded inline-block mt-0.5 ${
                      submission.status === 'approved' ? 'bg-green-100 text-green-800' :
                      submission.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {submission.status}
                    </span>
                  )}
                </div>
                
                {/* Edit Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditSubmission(submission);
                  }}
                  className="bg-[var(--pbfs-blue)] hover:bg-[var(--pbfs-navy)] text-white px-2 sm:px-3 py-1.5 rounded-lg text-xs sm:text-sm font-semibold transition-all flex items-center gap-1 flex-shrink-0"
                >
                  <span>✏️</span>
                  <span className="hidden sm:inline">Edit</span>
                </button>
              </div>
              ))}
              {/* Paging control */}
              <UserPagingControls paging={paging} onLoadMore={onLoadMore} isLoading={isLoadingSubmissions} />
            </>
          )}
        </div>
      )}
    </div>
  );
};

const UserPagingControls = ({ paging, onLoadMore, isLoading }) => {
  if (!paging) return null;
  const shown = Math.min((paging.offset || 0) + (paging.limit || 0), paging.total || 0);
  const remaining = Math.max(0, (paging.total || 0) - shown);
  if (!paging.has_more) return null;
  return (
    <div className="pt-2 flex items-center justify-between">
      <div className="text-xs text-gray-500">
        Showing {shown} of {paging.total || 0}
      </div>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          if (onLoadMore) onLoadMore();
        }}
        disabled={!!isLoading}
        className="text-xs font-semibold px-3 py-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 transition-colors disabled:opacity-60"
      >
        {isLoading ? 'Loading…' : `Load more (${remaining}+ remaining)`}
      </button>
    </div>
  );
};

export default TeamHierarchyModal;

